/**
 * UTVCovers Editorial Article — Interactivity
 * - Table of contents (desktop sidebar + mobile drawer)
 * - Reading progress bar
 * - Image lightbox
 * - Checklist state persistence
 * - Analytics events
 */

(function () {
  'use strict';

  /* ── Utilities ── */

  function slugify(text) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function getHeaderOffset() {
    var header = document.querySelector('header, .header, [data-section-type="header"]');
    return header ? header.getBoundingClientRect().height + 16 : 80;
  }

  function trackEditorial(action, data) {
    try {
      document.dispatchEvent(new CustomEvent('editorial:' + action, { detail: data }));
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'editorial_' + action, editorial_data: data });
      }
    } catch (e) { /* silent */ }
  }

  /* ═══════════════════════════════════════
     TABLE OF CONTENTS
     ═══════════════════════════════════════ */

  function initTOC() {
    var article = document.querySelector('[data-editorial-article]');
    if (!article) return;

    var stream = article.querySelector('.utv-editorial__stream');
    if (!stream) return;

    var tocDesktop = article.querySelector('[data-editorial-toc-list]');
    var tocMobileWrap = document.querySelector('[data-editorial-toc-mobile]');
    var tocMobileInner = document.querySelector('[data-editorial-toc-mobile-inner]');

    var wordCount = parseInt(article.dataset.wordCount || '0', 10);
    var headings = stream.querySelectorAll('h2, h3');
    var h2Count = stream.querySelectorAll('h2').length;

    if (wordCount < 400 || h2Count < 3) return;

    var usedIds = {};
    var tocItems = [];
    var h2Index = 0;

    headings.forEach(function (heading) {
      var text = heading.textContent.trim();
      if (!text) return;
      var baseId = slugify(text);
      var id = baseId;
      var counter = 1;
      while (usedIds[id]) { id = baseId + '-' + counter; counter++; }
      usedIds[id] = true;
      heading.id = id;
      var level = heading.tagName === 'H2' ? 2 : 3;
      if (level === 2) h2Index++;
      tocItems.push({ id: id, text: text, level: level, el: heading, num: level === 2 ? h2Index : 0 });
    });

    if (tocItems.length < 2) return;

    // Desktop: numbered links
    if (tocDesktop) {
      tocDesktop.innerHTML = tocItems.map(function (item) {
        var cls = 'utv-editorial__toc-link';
        if (item.level === 3) cls += ' utv-editorial__toc-link--h3';
        var numHtml = item.num > 0
          ? '<span class="utv-editorial__toc-num">' + (item.num < 10 ? '0' : '') + item.num + '</span>'
          : '';
        return '<li class="utv-editorial__toc-item">' +
          '<a href="#' + item.id + '" class="' + cls + '" data-toc-target="' + item.id + '">' +
          numHtml + '<span class="utv-editorial__toc-label">' + item.text + '</span></a></li>';
      }).join('');
    }

    // Mobile: horizontal pill strip (H2s only)
    if (tocMobileInner) {
      var h2Items = tocItems.filter(function (i) { return i.level === 2; });
      tocMobileInner.innerHTML = h2Items.map(function (item) {
        return '<a href="#' + item.id + '" class="utv-editorial__toc-pill" data-toc-target="' + item.id + '">' +
          '<span class="utv-editorial__toc-pill-num">' + (item.num < 10 ? '0' : '') + item.num + '</span>' +
          item.text + '</a>';
      }).join('');
      if (tocMobileWrap) tocMobileWrap.style.display = '';
    }

    // Smooth scroll
    function handleTocClick(e) {
      var target = e.target.closest('[data-toc-target]');
      if (!target) return;
      e.preventDefault();
      var heading = document.getElementById(target.dataset.tocTarget);
      if (!heading) return;
      var top = heading.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - 50;
      window.scrollTo({ top: top, behavior: 'smooth' });
      trackEditorial('toc', { action: 'click', target: target.dataset.tocTarget });
    }

    if (tocDesktop) tocDesktop.addEventListener('click', handleTocClick);
    if (tocMobileInner) tocMobileInner.addEventListener('click', handleTocClick);

    // Active state tracking
    function setActive(id) {
      // Desktop
      article.querySelectorAll('[data-toc-target]').forEach(function (link) {
        var isActive = link.dataset.tocTarget === id;
        link.classList.toggle('utv-editorial__toc-link--active', isActive);
        link.classList.toggle('utv-editorial__toc-pill--active', isActive);
      });
      // Auto-scroll mobile strip to keep active pill visible
      if (tocMobileInner) {
        var activePill = tocMobileInner.querySelector('.utv-editorial__toc-pill--active');
        if (activePill) {
          var containerRect = tocMobileInner.getBoundingClientRect();
          var pillRect = activePill.getBoundingClientRect();
          if (pillRect.left < containerRect.left || pillRect.right > containerRect.right) {
            activePill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        }
      }
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) setActive(entry.target.id);
      });
    }, { rootMargin: '-' + getHeaderOffset() + 'px 0px -60% 0px', threshold: 0 });

    tocItems.forEach(function (item) { observer.observe(item.el); });

    trackEditorial('toc', { action: 'eligible', h2_count: h2Count, word_count: wordCount, toc_items: tocItems.length });
  }

  /* ═══════════════════════════════════════
     READING PROGRESS BAR
     ═══════════════════════════════════════ */

  function initProgress() {
    var bar = document.querySelector('[data-editorial-progress]');
    if (!bar) return;

    var article = document.querySelector('[data-editorial-article]');
    if (!article) return;

    function update() {
      var rect = article.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      if (total <= 0) { bar.style.width = '100%'; return; }
      var scrolled = -rect.top;
      var pct = Math.max(0, Math.min(100, (scrolled / total) * 100));
      bar.style.width = pct + '%';
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ═══════════════════════════════════════
     IMAGE LIGHTBOX
     ═══════════════════════════════════════ */

  function initLightbox() {
    var dialog = document.querySelector('[data-editorial-lightbox-dialog]');
    if (!dialog) return;

    var img = dialog.querySelector('.utv-editorial-lightbox__img');
    var caption = dialog.querySelector('.utv-editorial-lightbox__caption');
    var closeBtns = dialog.querySelectorAll('[data-editorial-lightbox-close]');

    function openLightbox(src, alt) {
      img.src = src;
      img.alt = alt || '';
      caption.textContent = alt || '';
      dialog.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      dialog.removeAttribute('open');
      document.body.style.overflow = '';
      img.src = '';
    }

    // Attach to all lightbox triggers
    document.querySelectorAll('[data-editorial-lightbox]').forEach(function (trigger) {
      trigger.style.cursor = 'pointer';
      trigger.addEventListener('click', function () {
        var src = trigger.dataset.editorialLightbox;
        var alt = trigger.dataset.editorialCaption || trigger.querySelector('img')?.alt || '';
        openLightbox(src, alt);
      });
    });

    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', closeLightbox);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && dialog.hasAttribute('open')) {
        closeLightbox();
      }
    });
  }

  /* ═══════════════════════════════════════
     CHECKLIST PERSISTENCE
     ═══════════════════════════════════════ */

  function initChecklists() {
    var checklists = document.querySelectorAll('[data-editorial-checklist]');
    if (!checklists.length) return;

    // Use article URL as storage key
    var storageKey = 'utv-editorial-checklist:' + window.location.pathname;

    function loadState() {
      try {
        return JSON.parse(localStorage.getItem(storageKey)) || {};
      } catch (e) {
        return {};
      }
    }

    function saveState(state) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (e) { /* silent */ }
    }

    var state = loadState();

    checklists.forEach(function (list) {
      var boxes = list.querySelectorAll('[data-checklist-index]');
      boxes.forEach(function (box) {
        var idx = box.dataset.checklistIndex;

        // Restore state
        if (state[idx]) {
          box.setAttribute('aria-checked', 'true');
        }

        // Toggle on click
        box.addEventListener('click', function () {
          var checked = box.getAttribute('aria-checked') === 'true';
          box.setAttribute('aria-checked', checked ? 'false' : 'true');
          state[idx] = !checked;
          saveState(state);
        });

        // Toggle on Enter/Space
        box.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            box.click();
          }
        });
      });
    });
  }

  /* ═══════════════════════════════════════
     INIT
     ═══════════════════════════════════════ */

  function init() {
    initTOC();
    initProgress();
    initLightbox();
    initChecklists();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
